import {
    Box,
    Paper,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    CalendarToday as CalendarIcon,
    EmojiEvents as TargetsIcon
} from "@mui/icons-material";
import {useState} from "react";
import NoteDialog from "./NoteDialog";
import moment from "moment";
import {deleteTarget, updateClientTarget} from "../../services/TraineeService";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {RTL} from "../../cache/CacheRtl";

const ClientTarget = ({target, reload, coach = false}) => {
    const [editDialog, setEditDialog] = useState(false);
    const {notificationHandler} = useMessageContext();

    const handleDeleteTarget = () => {
        deleteTarget(target.id)
            .then(reload)
            .then(() => setEditDialog(false))
            .then(() => notificationHandler.success("מטרה נמחקה בהצלחה"))
            .catch(e => {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            })
    }

    const handleUpdateTarget = (newTarget) => {
        const body = {
            id: newTarget.id,
            message: newTarget.message,
        };

        updateClientTarget(body)
            .then(reload)
            .then(() => setEditDialog(false))
            .then(() => notificationHandler.success("מטרה נשמרה בהצלחה"))
            .catch(e => {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
            })
    }

    return (
        <>
            <NoteDialog
                approve={handleUpdateTarget}
                initValue={target}
                close={() => setEditDialog(false)}
                isOpen={editDialog}
                coach={coach}
            />

            {target && coach && (
              <RTL>
                <Paper
                    dir="rtl"
                    sx={{
                        backgroundColor: coachT.white,
                        border: `1px solid ${coachT.border}`,
                        borderRadius: coachT.rs,
                        transition: "all .14s",
                        overflow: "hidden",
                        "&:hover": {
                            borderColor: coachT.green,
                            boxShadow: "0 2px 10px rgba(15,20,40,.06)",
                            transform: "translateY(-1px)",
                        }
                    }}
                    elevation={0}
                >
                    {/* Top row: date + actions */}
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px 6px"}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                            <TargetsIcon sx={{color: coachT.green, fontSize: 16}}/>
                            <Typography sx={{fontSize: 11.5, fontWeight: 600, color: coachT.t3}}>
                                {moment.unix(target.createdDate).format("DD/MM/YY HH:mm")}
                            </Typography>
                        </Box>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.25}}>
                            <Tooltip title="עריכת מטרה">
                                <IconButton onClick={() => setEditDialog(true)} size="small"
                                    sx={{color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                    <EditIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="מחיקת מטרה">
                                <IconButton onClick={handleDeleteTarget} size="small"
                                    sx={{color: coachT.t2, "&:hover": {background: coachT.redLight, color: coachT.red}}}>
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    {/* Goal text */}
                    <Box sx={{padding: "0 14px 14px"}}>
                        <Typography sx={{fontSize: 14, lineHeight: "21px", fontWeight: 500, color: coachT.text, whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                            {target.message}
                        </Typography>
                    </Box>
                </Paper>
              </RTL>
            )}

            {target && !coach && (
              <RTL>
                <Paper
                    dir="rtl"
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: "#CBD5E1",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
                        }
                    }}
                    elevation={0}
                >
                    {/* Target Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            backgroundColor: "#F8FAFC",
                            borderBottom: "1px solid #E2E8F0"
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <CalendarIcon sx={{ color: "#64748B", fontSize: "16px" }} />
                            <Typography sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                color: "#64748B"
                            }}>
                                {moment.unix(target.createdDate).format("DD/MM/YY HH:mm")}
                            </Typography>
                        </Box>

                        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Tooltip title="עריכת מטרה">
                                <IconButton
                                    onClick={() => setEditDialog(true)}
                                    sx={{
                                        color: "#64748B",
                                        "&:hover": {
                                            backgroundColor: "#E2E8F0",
                                            color: "#475569"
                                        }
                                    }}
                                    size="small"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="מחיקת מטרה">
                                <IconButton
                                    onClick={handleDeleteTarget}
                                    sx={{
                                        color: "#EF4444",
                                        "&:hover": {
                                            backgroundColor: "#FEF2F2",
                                            color: "#DC2626"
                                        }
                                    }}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Target Content */}
                    <Box sx={{ padding: "16px" }}>
                        <Typography sx={{
                            fontSize: "14px",
                            lineHeight: "20px",
                            color: "#1E293B",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word"
                        }}>
                            {target.message}
                        </Typography>
                    </Box>
                </Paper>
              </RTL>
            )}
        </>
    );
};

export default ClientTarget;